import React from "react";
import { Lightbulb, Timer } from "lucide-react";
import { cn } from "@/lib/utils";

// Configurable time limit for the problem statement
const PROBLEM_TIME_LIMIT = "25 minutes";

interface ProblemStatementProps {
  instructionVisible: boolean;
  problemStatement: string;
  problemTitle?: string;
  instructions?: string;
  className?: string;
  isPopup?: boolean;
}

const ProblemStatement: React.FC<ProblemStatementProps> = ({
  instructionVisible,
  problemStatement,
  problemTitle,
  instructions = "Read the question given below thoroughly, and ask any doubts if needed before proceeding to the next coding round.",
  className,
  isPopup = false,
}) => {
  return (
    <div
      className={cn(
        "flex flex-col items-start p-6 md:p-3 lg:p-4 xl:p-6 gap-4 md:gap-4 lg:gap-4 xl:gap-4 flex-1 w-full bg-white",
        className
      )}
    >
      {instructionVisible && (
        <div className="flex items-start gap-3 md:gap-2 lg:gap-2 xl:gap-3 p-4 md:p-2 lg:p-3 xl:p-4 rounded-lg w-full bg-[#FFF4E6] border border-[#FFE4C2]">
          <Lightbulb className="w-5 h-5 md:w-3 md:h-3 lg:w-4 lg:h-4 xl:w-5 xl:h-5 shrink-0 mt-0.5 text-[#3D2700]" />
          <p className="text-[#3D2700] font-dm-sans text-base md:text-[10px] lg:text-[11px] xl:text-[15px] font-normal leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px]">
            {instructions}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-6 md:gap-3 lg:gap-4 xl:gap-6 w-full">
        {/* Header Section: Title and Time */}
        <div className="flex justify-between items-center w-full flex-wrap gap-4 md:gap-2 lg:gap-3 xl:gap-4">
          <h2 className="text-black font-dm-sans text-[18px] md:text-[12px] lg:text-[14px] xl:text-[18px] font-medium leading-[26px] md:leading-[18px] lg:leading-[20px] xl:leading-[26px] tracking-[0.27px]">
            Problem Statement
          </h2>

          <div className={cn(
            "flex items-center gap-2 md:gap-1 lg:gap-1.5 xl:gap-2",
            isPopup && "mr-12 md:mr-8 lg:mr-10 xl:mr-12"
          )}>
            <Timer className="w-5 h-5 md:w-3 md:h-3 lg:w-4 lg:h-4 xl:w-5 xl:h-5 text-[#68696B]" />
            <span className="text-[#68696B] font-dm-sans text-base md:text-[10px] lg:text-[11px] xl:text-[15px] font-normal leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px]">
              Time:
            </span>
            <span className="text-[#0C0F12] font-dm-sans text-base md:text-[10px] lg:text-[11px] xl:text-[15px] font-medium leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px]">
              {PROBLEM_TIME_LIMIT}
            </span>
          </div>
        </div>

        {/* Problem Text */}
        <div className="text-[#202326] font-dm-sans text-base md:text-[10px] lg:text-[11px] xl:text-[15px] font-normal leading-6 md:leading-4 lg:leading-5 xl:leading-6 tracking-[0.08px] whitespace-pre-wrap">
          {problemTitle && (
            <>
              <div className="mb-4 md:mb-2 lg:mb-3 xl:mb-4">
                {problemTitle}
              </div>
            </>
          )}
          {problemStatement}
        </div>
      </div>
    </div>
  );
};

export default ProblemStatement;
